import Vue from "vue";
import store from "../store/store"; //
import Router from "vue-router";
import { browseRoutes } from "./browse_router";
import { detailRoutes } from "./detail_router";
import { searchRoutes } from "./search_router";
import { authRoutes } from "./auth_router";
import { downloadRoutes } from "./download_router";
import { newsRoutes } from "./news_router";
import TairHome from "@/views/TairHome";
import Profile from "@/views/Profile";
import PageNotFound from "../components/common/PageNotFound";
import SearchResults from "@/views/search/SearchResults";
import Blast from "../views/tools/Blast.vue";
import BlastResults from "../views/tools/BlastResults.vue";
import Patmatch from "../views/tools/Patmatch.vue";
import PatmatchResults from "../views/tools/PatmatchResults.vue";
import MotifFinder from "../views/tools/MotifFinder.vue";
import SyntenyViewer from "../views/tools/SyntenyViewer.vue";
import ChromosomeMap from "../views/tools/ChromosomeMap.vue";
import GoTermEnrichment from "../views/tools/GoTermEnrichment.vue";
import OrganizationEditDetail from "@/views/edit/OrganizationEditDetail";
import PersonEditDetail from "@/views/edit/PersonEditDetail";
import RegistrationDetail from "@/views/edit/RegistrationDetail";
import Sequences from "@/views/search/Sequences";
import GeneClassSymbolRegistration from "@/views/submit/GeneClassSymbolRegistration";
import GeneClassSymbolRegistrationForm from "@/views/submit/GeneClassSymbolRegistrationForm";
import GeneClassSymbolDetailRegistrationForm from "@/views/submit/GeneClassSymbolDetailRegistrationForm";
import GeneSymbolReviewList from "@/views/curator/GeneSymbolReviewList";
import GeneSymbolReview from "@/views/curator/GeneSymbolReview";
import PermissionDenied from "../components/common/PermissionDenied";
import redirectMapping from "./redirectMapping";
import LinkToOrcid from "@/views/orcid/LinkToOrcid";
import LoginPage from "@/views/orcid/Login";

Vue.use(Router);

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/",
      name: "TairHome",
      component: TairHome,
    },
    {
      path: "/index.jsp",
      redirect: { name: "TairHome" },
    },
    {
      path: "/tools/blast",
      name: "Blast",
      component: Blast,
      meta: { title: "BLAST - TAIR" },
    },
    {
      path: "/tools/blast/results/:jobId",
      name: "BlastResults",
      component: BlastResults,
      meta: { title: "BLAST Results - TAIR" },
    },
    {
      path: "/tools/chromosome_map",
      name: "ChromosomeMap",
      component: ChromosomeMap,
      meta: { title: "Chromosome Map Tool - TAIR" },
    },
    {
      path: "/tools/patmatch",
      name: "Patmatch",
      component: Patmatch,
      meta: { title: "Patmatch - TAIR" },
    },
    {
      path: "/tools/patmatch/results/:jobId",
      name: "PatmatchResults",
      component: PatmatchResults,
      meta: { title: "Patmatch Results - TAIR" },
    },
    {
      path: "/tools/go_term_enrichment",
      name: "GoTermEnrichment",
      component: GoTermEnrichment,
      meta: { title: "GO Term Enrichment - TAIR" },
    },
    {
      path: "/tools/motiffinder",
      name: "MotifFinder",
      component: MotifFinder,
      meta: { title: "Motif Analysis - TAIR" },
    },
    {
      path: "/tools/synteny_viewer",
      name: "SyntenyViewer",
      component: SyntenyViewer,
      meta: { title: "Synteny Viewer - TAIR" },
    },
    authRoutes,
    browseRoutes,
    detailRoutes,
    downloadRoutes,
    newsRoutes,
    searchRoutes,
    {
      path: "/access-denied",
      name: "AccessControl",
      component: () => import("@/views/AccessControl.vue"),
      meta: { title: "Subscription Required - TAIR" },
    },
    {
      path: "/profile",
      name: "Profile",
      component: Profile,
      meta: { title: "My Profile - TAIR" },
    },
    {
      path: "/sequences",
      name: "Sequences",
      component: Sequences,
      meta: { title: "Sequence - TAIR" },
    },
    {
      path: "/results",
      name: "SearchResults",
      component: SearchResults,
    },

    {
      path: "/portals/overview",
      name: "PortalsOverview",
      component: () => import("@/views/portals/PortalsOverview.vue"),
    },
    {
      path: "/submit/overview",
      name: "SubmitOverview",
      component: () => import("@/views/submit/SubmissionOverview.vue"),
    },
    {
      path: "/submit/gene_class_symbol_registration",
      name: "GeneClassSymbolRegistration",
      component: GeneClassSymbolRegistration,
      meta: { title: "Register a Gene Class Symbol - TAIR" },
    },
    {
      path: "/submit/gene_class_symbol_registration/form",
      name: "GeneClassSymbolRegistrationForm",
      component: GeneClassSymbolRegistrationForm,
      meta: { title: "Gene Class Symbol Registration - TAIR" },
      beforeEnter: (to, from, next) => {
        const isLoggedIn = store.getters["authProfile/isLoggedIn"];
        if (isLoggedIn) {
          next();
        } else {
          next("/access-denied");
        }
      },
    },
    {
      path: "/submit/gene_class_symbol_detail_registration/form",
      name: "GeneClassSymbolDetailRegistrationForm",
      component: GeneClassSymbolDetailRegistrationForm,
      meta: { title: "Gene Class Symbol Detail Registration - TAIR" },
      beforeEnter: (to, from, next) => {
        const isLoggedIn = store.getters["authProfile/isLoggedIn"];
        if (isLoggedIn) {
          next();
        } else {
          next("/access-denied");
        }
      },
    },
    {
      path: "/curator/gene_symbol_pending",
      name: "GeneSymbolReviewList",
      component: GeneSymbolReviewList,
      meta: { title: "Gene Symbol Curator Review - TAIR" },
      beforeEnter: (to, from, next) => {
        const isLoggedIn = store.getters["authProfile/isLoggedIn"];
        const isTairCurator = store.getters["authProfile/getIsTairCurator"];
        if (isLoggedIn && isTairCurator) {
          next();
        } else if (!isLoggedIn) {
          next("/access-denied");
        } else {
          next("/permission-denied");
        }
      },
    },
    {
      path: "/curator/gene_symbol_review",
      name: "GeneSymbolReview",
      component: GeneSymbolReview,
      meta: { title: "Gene Symbol Review - TAIR" },
      beforeEnter: (to, from, next) => {
        const isLoggedIn = store.getters["authProfile/isLoggedIn"];
        const isTairCurator = store.getters["authProfile/getIsTairCurator"];
        if (isLoggedIn && isTairCurator) {
          next();
        } else if (!isLoggedIn) {
          next("/access-denied");
        } else {
          next("/permission-denied");
        }
      },
    },
    {
      path: "/tools/microarray_retired",
      name: "MicroarrayRetired",
      component: () => import("@/views/tools/MicroarrayRetired.vue"),
      meta: { title: "Microarray Elements Tool Retired - TAIR" },
    },
    {
      path: "/tools/mapviewer",
      name: "MapViewerRetired",
      component: () => import("@/views/tools/MapViewerRetired.vue"),
      meta: { title: "MapViewer Retired - TAIR" },
    },
    {
      path: "/tools/overview",
      name: "ToolsOverview",
      component: () => import("@/views/tools/ToolsOverview.vue"),
    },
    {
      path: "/about/overview",
      name: "AboutOverview",
      component: () => import("@/views/about/AboutOverview.vue"),
    },
    {
      path: "/register",
      name: "RegistrationDetail",
      component: RegistrationDetail,
    },
    {
      path: "/help",
      name: "HelpOverview",
      component: () => import("@/views/about/HelpOverview.vue"),
    },
    {
      path: "/contact",
      name: "Contact",
      component: () => import("@/views/about/Contact.vue"),
    },
    {
      path: "/accessibility",
      name: "Accessibility",
      component: () => import("@/views/Accessibility.vue"),
    },
    {
      path: "/sitemap",
      name: "SiteMap",
      component: () => import("@/views/SiteMap.vue"),
      meta: { title: "Site Map - TAIR" },
    },
    // {
    //   path: "/results",
    //   name: "SearchResults",
    //   component: SearchResults
    // },
    {
      // TAIR3-1013: the old Sequence Bulk Download page never worked; bulk
      // sequences come from Gene Search -> check genes -> "Get Sequences".
      path: "/type_sequences/:ids*",
      redirect: { name: "GeneSearch" },
    },

    {
      path: "/browse/gene_family/:key",
      name: "GeneFamily",
      component: () => import("@/views/detail/GeneFamily.vue"),
    },
    {
      path: "/browse/microarray_experiments/:key",
      name: "MicroarrayExperiment",
      component: () => import("@/views/detail/MicroarrayExperimentDetail.vue"),
    },

    {
      path: "/edit/person",
      name: "PersonEditDetail",
      component: PersonEditDetail,
      beforeEnter: (to, from, next) => {
        // Enable local UI-only development with ?mockPersonEdit or localStorage flag (dev builds only)
        const isDevBuild = process.env.NODE_ENV !== "production";
        const hasMockQuery = to.query.mockPersonEdit !== undefined;
        const hasMockStorage =
          window.localStorage.getItem("mockPersonEdit") === "1";
        const devBypass = isDevBuild && (hasMockQuery || hasMockStorage);

        if (devBypass) {
          next(); // Allow access in dev mode with mock data
          return;
        }

        const urlCommunityId = to.query.key;
        const storeCommunityId = store.getters["authProfile/getCommunityId"];
        const isTairCurator = store.getters["authProfile/getIsTairCurator"];
        if (urlCommunityId == storeCommunityId || isTairCurator) {
          next(); // Proceed as normal
        } else {
          next("/permission-denied"); // Redirect to a permission denied page if neither condition is met
        }
      },
    },
    {
      path: "/edit/organization",
      name: "OrganizationEditDetail",
      component: OrganizationEditDetail,
      meta: { title: "Edit Organization - TAIR" },
      beforeEnter: (to, from, next) => {
        const urlCommunityId = to.query.key;
        const isTairCurator = store.getters["authProfile/getIsTairCurator"];
        const affiliations = store.getters["authProfile/getAffiliations"];
        const urlCommunityIdNumber = Number(urlCommunityId);
        let hasAccess = false;
        if (affiliations) {
          hasAccess =
            affiliations.includes(urlCommunityIdNumber) || isTairCurator;
        } else {
          hasAccess = isTairCurator;
        }
        if (hasAccess) {
          next(); // Proceed as normal
        } else {
          next("/permission-denied"); // Redirect to a permission denied page if no conditions are met
        }
      },
    },
    {
      path: "/permission-denied",
      name: "PermissionDenied",
      component: PermissionDenied,
      meta: { title: "Permission Denied - TAIR" },
    },
    {
      path: "/download_files/*",
      beforeEnter: (to, from, next) => {
        const newPath = to.path.replace(
          "/download_files/",
          "/download/file?path="
        );
        next({ path: newPath });
      },
    },
    {
      path: "/pagenotfound",
      name: "PageNotFound",
      component: PageNotFound,
      meta: { title: "Page Not Found - TAIR" },
    },
    {
      path: "/linktoorcid",
      name: "LinkToOrcid",
      component: LinkToOrcid,
      meta: { title: "Link ORCID - TAIR" },
    },
    {
      path: "/login",
      name: "LoginPage",
      component: LoginPage,
      meta: { title: "Login - TAIR" },
    },
    //redirects for external URLS
    {
      path: "/patmatch/release_notes.jsp",
      beforeEnter(to, from, next) {
        // Put the full page URL including the protocol http(s) below
        window.location.replace(
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217191/PatMatch+Notes"
        );
      },
    },
    {
      path: "/portals/genAnnotation/functional_annotation/go.jsp",
      beforeEnter(to, from, next) {
        // Redirect to the new Gene Ontology Annotations page
        window.location.replace(
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/132055060/Gene+Ontology+Annotations"
        );
      },
    },
    {
      path: "/help/faq.jsp",
      beforeEnter(to, from, next) {
        // Check if the hash part of the URL matches 'jobs' and redirect appropriately
        if (to.hash === "#jobs") {
          window.location.replace(
            "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216127/FAQ#How-do-I-post-a-job-opening-at-TAIR?"
          );
        }
      },
    },
    {
      path: "/portals/nomenclature/index.jsp",
      beforeEnter(to, from, next) {
        // Put the full page URL including the protocol http(s) below
        window.location.replace(
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42215945/Arabidopsis+Nomenclature"
        );
      },
    },
    {
      path: "/patmatch/release_notes.jsp",
      beforeEnter(to, from, next) {
        // Put the full page URL including the protocol http(s) below
        window.location.replace(
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217191/PatMatch+Notes"
        );
      },
    },
    {
      path: "/portals/masc/projects.jsp",
      beforeEnter() {
        window.location.href =
          "https://www.arabidopsisresearch.org/index.php/en/";
      },
    },
    {
      path: "/browse/Cereon/index.jsp",
      beforeEnter() {
        window.location.href =
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216916/Monsanto+Arabidopsis+Polymorphism+and+Ler+Sequence+Collections+Historical+Record";
      },
    },
    {
      path: "/tools/go_term_enrichment.jsp",
      redirect: { name: "GoTermEnrichment" },
    },
    {
      path: "/cgi-bin/syntenyviewer2/showSynteny.pl",
      redirect: { name: "SyntenyViewer" },
    },
    {
      path: "/servlets/mapper",
      redirect: (to) => ({
        name: "MapViewerRetired",
        query: to.query.value ? { value: to.query.value } : {},
      }),
    },
    {
      path: "/phylogenes.arabidopsis.org/",
      beforeEnter() {
        window.location.href = `${process.env.VUE_APP_PHYLOGENES}`;
      },
    },
    {
      path: "/biocyc/index.jsp",
      beforeEnter() {
        window.location.href =
          "https://pmn.plantcyc.org/organism-summary?object=ARA";
      },
    },
    {
      path: "/tools/bulk/motiffinder/index.jsp",
      redirect: { name: "MotifFinder" },
    },
    {
      path: "/tools/bulk/locushistory/index.jsp",
      beforeEnter() {
        window.location.href =
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217397/Bulk+downloading+Gene+Data";
      },
    },
    {
      path: "/Blast/index.jsp",
      beforeEnter() {
        window.location.href = "https://www.arabidopsis.org/tools/blast/";
      },
    },
    {
      path: "/cgi-bin/patmatch/nph-patmatch.pl",
      beforeEnter() {
        window.location.href = "https://www.arabidopsis.org/tools/Patmatch";
      },
    },
    {
      path: "/jsp/ChromosomeMap/tool.jsp",
      redirect: { name: "ChromosomeMap" },
    },
    {
      // TAIR3-662: the Microarray Elements mapping tool is retired. Must sit
      // before the /tools/bulk/* catch-all below so it wins the match.
      path: "/tools/bulk/microarray/*",
      redirect: { name: "MicroarrayRetired" },
    },
    {
      path: "/tools/bulk/microarray",
      redirect: { name: "MicroarrayRetired" },
    },
    {
      path: "/tools/bulk/*",
      beforeEnter(to, from, next) {
        if (to.path !== "/tools/bulk/protein/index.jsp") {
          window.location.href =
            "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217397/Bulk+downloading+Gene+Data";
        } else {
          next("/search/protein");
        }
      },
    },
    {
      path: "/submit/ExternalLinkSubmission.jsp",
      beforeEnter() {
        window.location.href =
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216080/Data+Submission#External-Links";
      },
    },
    {
      path: "/submit/genefamily_submission.jsp",
      beforeEnter() {
        // Redirect to a not found or specific error page
        window.location.href = "/pagenotfound"; // Change this URL to wherever you handle error or not found cases
      },
    },
    {
      path: "/doc/submit/functional_annotation/123",
      beforeEnter() {
        window.location.href =
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216080/Data+Submission#Gene-Function-Data";
      },
    },
    {
      path: "/submit/marker_submission.jsp",
      beforeEnter() {
        window.location.href =
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216080/Data+Submission#Marker-and-Polymorphism-Data";
      },
    },
    {
      path: "/submit/phenotype_submission.jsp",
      beforeEnter() {
        window.location.href =
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216080/Data+Submission#Phenotypes";
      },
    },
    {
      path: "/submit/protocol_submission.jsp",
      beforeEnter() {
        window.location.href =
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216080/Data+Submission#Protocols";
      },
    },
    {
      path: "/submit/gene_annotation.submission.jsp",
      beforeEnter() {
        window.location.href =
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216080/Data+Submission#Gene-Structure-Additions/Modifications";
      },
    },
    {
      path: "/portals/genAnnotation/genome_snapshot.jsp",
      beforeEnter(to, from, next) {
        // Redirect to the Genome Snapshot page
        window.location.replace(
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42215974/Genome+Snapshot"
        );
      },
    },
    {
      path: "/about/tairpubs.jsp",
      beforeEnter(to, from, next) {
        // Redirect to the TAIR Publications page
        window.location.replace(
          "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42216389/TAIR+Publications"
        );
      },
    },
    {
      path: "*",
      beforeEnter: (to, from, next) => {
        const pathSegments = to.path.split("/").filter((segment) => segment);
        const lastSegment = pathSegments[pathSegments.length - 1];
        const baseRoute =
          pathSegments.length > 1
            ? pathSegments.slice(0, -1).join("/")
            : to.path;

        const mappings = redirectMapping[baseRoute] || redirectMapping[to.path];
        if (mappings) {
          let mapping;
          // Check if we need to handle path segments
          if (!isNaN(parseInt(lastSegment))) {
            // It's a numeric ID, find the mapping that handles IDs
            mapping = mappings.find(
              (m) =>
                m.type === "person_detail" || m.type === "organization_detail"
            );
            if (mapping && mapping.targetPath) {
              next({ path: mapping.targetPath(lastSegment) });
              return;
            }
          } else {
            // Handle based on query parameters
            mapping = mappings.find(
              (m) => to.query.action === m.action && to.query.type === m.type
            );
            if (mapping && mapping.targetPath) {
              next({ path: mapping.targetPath(to.query) });
              return;
            }
            // do accession stuff
            if (to.query.accession) {
              mapping = mappings.find((m) => m.isDefault); // Adjusted to find 'isDefault'
              if (mapping && mapping.targetPath) {
                const redirectPath = mapping.targetPath(to.query);
                next({ path: redirectPath });
                return;
              }
            }
          }
          next(); // No specific mapping found, continue with default behavior
        } else {
          next({ name: "PageNotFound" }); // No mappings for this path, continue with default behavior
        }
      },
    },
  ],
});

// Each view sets document.title in mounted(), but many never do, and since
// the SPA never reloads they kept the previous page's title (e.g. Download
// pages showing "TAIR Help"). Reset the title on every path change: to the
// deepest matched route's meta.title, or a generic default. afterEach runs
// before the new view mounts, so views that set their own (e.g. dynamic)
// titles still override it. Query/hash-only changes are left alone.
router.afterEach((to, from) => {
  if (to.path === from.path && from.matched.length) return;
  const withTitle = [...to.matched].reverse().find((r) => r.meta.title);
  document.title = withTitle ? withTitle.meta.title : "TAIR";
});

export default router;
